import { useEffect, useRef, useState } from 'react';
import { ALL_GRAPHS } from '@/components/GraphSelector';
import { graphDisplayLabel, useGraphs } from '@/lib/useGraphs';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/Button';

/** Multi-graph scope picker. A disclosure checklist:
 *  "All graphs (union)" is exclusive of individual picks (checking it clears
 *  them and vice versa); any set of named graphs can be checked together.
 *  Closes on outside click. Selection is the host surface's read-scope
 *  contract, and the control is deliberately IDENTICAL on every surface that
 *  offers graph selection (GRAPH_SELECTION_SURFACES.md) — no per-page
 *  variants, so the affordance reads the same wherever it appears. */
export function GraphMultiPicker({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const graphs = useGraphs();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const named = (graphs.data ?? []).filter((g) => g.bare !== ALL_GRAPHS);
  const union = selected.includes(ALL_GRAPHS);
  const summary = union
    ? 'All graphs'
    : selected.length === 0
      ? 'No graph picked'
      : selected.length === 1
        ? (named.find((g) => g.bare === selected[0])
            ? graphDisplayLabel(named.find((g) => g.bare === selected[0])!)
            : selected[0])
        : `${selected.length} graphs`;

  const toggle = (bare: string) => {
    if (bare === ALL_GRAPHS) {
      onChange(union ? [] : [ALL_GRAPHS]);
      return;
    }
    const without = selected.filter((g) => g !== bare && g !== ALL_GRAPHS);
    onChange(selected.includes(bare) ? without : [...without, bare]);
  };

  return (
    <div ref={ref} className="relative">
      <label className="flex items-center gap-2 text-sm">
        <span className="text-tres-text-secondary">Graphs</span>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={cn(buttonVariants({ variant: 'secondary', size: 'md' }), 'min-w-[180px] text-left')}
          aria-expanded={open}
        >
          {summary}
        </button>
      </label>
      {open && (
        <div className="absolute right-0 z-20 mt-1 max-h-72 w-72 overflow-auto rounded-md border border-border bg-card p-2 shadow-lg">
          <label className="flex items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-muted">
            <input type="checkbox" checked={union} onChange={() => toggle(ALL_GRAPHS)} />
            <span className="font-medium">All graphs (union)</span>
          </label>
          <div className="my-1 border-t border-border" />
          {named.map((g) => (
            <label
              key={g.bare}
              className={cn(
                'flex items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-muted',
                union && 'opacity-50',
              )}
            >
              <input
                type="checkbox"
                checked={!union && selected.includes(g.bare)}
                onChange={() => toggle(g.bare)}
              />
              <span className="truncate" title={g.bare}>
                {graphDisplayLabel(g)}
              </span>
            </label>
          ))}
          {named.length === 0 && (
            <p className="px-1 py-1 text-xs text-tres-text-secondary">No named graphs found.</p>
          )}
        </div>
      )}
    </div>
  );
}
